import { APP_BASE_HREF } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, TransferState } from '@angular/core';
import { of, tap, type Observable } from 'rxjs';
import { DYNAMIC_ENVIRONMENT_STATE_KEY } from '../dynamic-environment';
import { type DynamicEnvironmentModel } from '../models/dynamic-environment.model';
import { type StaticEnvironmentModel } from '../models/static-environment.model';
import { STATIC_ENVIRONMENT } from '../static-environment';
import { WindowService } from './window.service';

@Injectable({
    providedIn: 'root',
})
export class EnvironmentService {
    private readonly appBaseHref = inject(APP_BASE_HREF, { optional: true });
    private readonly staticEnvironment = inject(STATIC_ENVIRONMENT);
    private readonly httpClient = inject(HttpClient);
    private readonly window = inject(WindowService);
    private readonly state = inject(TransferState);
    private configuration?: Observable<DynamicEnvironmentModel>;
    private dynamicEnvironment?: DynamicEnvironmentModel;

    public get production(): StaticEnvironmentModel['production'] {
        return this.staticEnvironment.production;
    }

    public get baseHref(): string {
        return this.appBaseHref ?? (this.dynamicEnvironment?.apiProtocol !== undefined
            ? `${this.dynamicEnvironment.apiProtocol}://${this.dynamicEnvironment.apiHost}:${this.dynamicEnvironment.apiPort}`
            : this.window.nativeWindow()?.location.origin ?? '');
    }

    public get appName(): StaticEnvironmentModel['appName'] {
        return this.staticEnvironment.appName;
    }

    public get coBrowsingUrl(): StaticEnvironmentModel['coBrowsingUrl'] {
        return this.staticEnvironment.coBrowsingUrl;
    }

    public get privatePath(): StaticEnvironmentModel['privateContentPath'] {
        return this.staticEnvironment.privateContentPath;
    }

    public get jsonapiPath(): StaticEnvironmentModel['jsonapiPath'] {
        return this.staticEnvironment.jsonapiPath;
    }

    public get restapiPath(): StaticEnvironmentModel['restapiPath'] {
        return this.staticEnvironment.restapiPath;
    }

    public get cookieBotId(): string {
        return this.dynamicEnvironment?.cookieBotId ?? '';
    }

    public get googleMapsKey(): string {
        return this.dynamicEnvironment?.googleMapsKey ?? '';
    }

    public get gtmUrl(): string {
        return this.dynamicEnvironment?.gtmUrl ?? '';
    }

    public get gtmId(): string {
        return this.dynamicEnvironment?.gtmId ?? '';
    }

    public get jsonLd(): StaticEnvironmentModel['jsonLd'] {
        return this.staticEnvironment.jsonLd;
    }

    public get metaTags(): StaticEnvironmentModel['metaTags'] {
        return this.staticEnvironment.metaTags;
    }

    public get routerBlacklistedUrls(): string {
        return this.dynamicEnvironment?.routerBlacklistedUrls ?? '';
    }

    public get sentryEnvironment(): DynamicEnvironmentModel['sentryEnvironment'] {
        return this.dynamicEnvironment?.sentryEnvironment ?? '';
    }

    public get sentryDsn(): DynamicEnvironmentModel['sentryDsn'] {
        return this.dynamicEnvironment?.sentryDsn ?? '';
    }

    public get sentryTracesSampleRate(): DynamicEnvironmentModel['sentryTracesSampleRate'] {
        return this.dynamicEnvironment?.sentryTracesSampleRate ?? 0;
    }

    public get sentryTracingOrigins(): string[] {
        return this.dynamicEnvironment?.sentryTracingOrigins?.split(',') ?? [];
    }

    public get sentryRelease(): DynamicEnvironmentModel['sentryRelease'] {
        return this.dynamicEnvironment?.sentryRelease ?? '';
    }

    public get workEnv(): string {
        return this.dynamicEnvironment?.workEnv ?? '';
    }

    public get faqApiPath(): string | undefined {
        return this.staticEnvironment.faqApiPath;
    }

    public get formApiPath(): StaticEnvironmentModel['formApiPath'] {
        return this.staticEnvironment.formApiPath;
    }

    public get publicFormsHostUrl(): StaticEnvironmentModel['publicFormsHostUrl'] {
        return this.staticEnvironment.publicFormsHostUrl;
    }

    public get placeholdersApiPath(): StaticEnvironmentModel['placeholdersApiPath'] {
        return this.staticEnvironment.placeholdersApiPath;
    }

    public get sessionTokenApiPath(): StaticEnvironmentModel['sessionTokenApiPath'] {
        return this.staticEnvironment.sessionTokenApiPath;
    }

    public get verboseLogs(): DynamicEnvironmentModel['verboseLogs'] {
        return this.dynamicEnvironment?.verboseLogs ?? false;
    }

    public get version(): string {
        return this.dynamicEnvironment?.version ?? '0.0.0';
    }

    public get unleashClientKey(): string {
        return this.dynamicEnvironment?.unleashClientKey ?? '';
    }

    public get errorNodeIds(): StaticEnvironmentModel['errorNodeIds'] {
        return this.staticEnvironment.errorNodeIds;
    }

    public get servicePhoneNumber(): StaticEnvironmentModel['servicePhoneNumber'] {
        return this.staticEnvironment.servicePhoneNumber;
    }

    public get errorPhoneNumber(): StaticEnvironmentModel['errorPhoneNumber'] {
        return this.staticEnvironment.errorPhoneNumber;
    }

    public load(): Observable<DynamicEnvironmentModel> {
        if (this.configuration !== undefined) {
            return this.configuration;
        }

        // ssr: check state with dynamic environment exists
        const dynamicEnvironmentState = this.state.get(DYNAMIC_ENVIRONMENT_STATE_KEY, undefined);

        if (dynamicEnvironmentState) {
            this.dynamicEnvironment = dynamicEnvironmentState;

            return of(dynamicEnvironmentState);
        }

        // non-ssr: load config.json on the client side
        const url = `${this.appBaseHref ?? ''}/assets/config.json`;

        this.configuration = this.httpClient.get<DynamicEnvironmentModel>(url).pipe(
            tap((dynamicEnvironment: DynamicEnvironmentModel): void => {
                this.dynamicEnvironment = dynamicEnvironment;
            }),
        );

        return this.configuration;
    }
}
